<template>
  <div class="design-tauri">
    <front-page-design-nav theme="transparent" />

    <section class="hero">
      <div class="hero__glow hero__glow--1" />
      <div class="hero__glow hero__glow--2" />
      <div class="hero__content">
        <img :src="logo" alt="BOX21" class="hero__logo" />
        <h1 class="hero__title">
          <span>{{ hero.line1 }}</span>
          <span>{{ hero.line2 }}</span>
          <span class="hero__accent">{{ hero.line3 }}</span>
        </h1>
        <p class="hero__tagline">{{ hero.tagline }}</p>
        <div class="hero__actions">
          <button type="button" class="btn btn--primary" @click="goToRegister">Get started</button>
          <button type="button" class="btn btn--ghost" @click="goToLogin">Login</button>
        </div>
      </div>
    </section>

    <section class="features">
      <div class="features__grid">
        <article v-for="pillar in pillars" :key="pillar.id" class="feature-card">
          <img :src="pillar.icon" :alt="pillar.title" class="feature-card__icon" />
          <h3>{{ pillar.title }}</h3>
          <div class="feature-card__tags">
            <span v-for="tag in pillar.tags" :key="tag">{{ tag }}</span>
          </div>
        </article>
      </div>
    </section>

    <section class="showcase">
      <article v-for="(item, i) in showcase" :key="item.id" :class="['showcase-row', { 'showcase-row--reverse': i % 2 === 1 }]">
        <div class="showcase-row__text">
          <h2>{{ item.title }}</h2>
          <p>{{ item.text }}</p>
        </div>
        <img :src="item.image" :alt="item.title" class="showcase-row__img" />
      </article>
    </section>

    <front-page-design-footer theme="dark" />
  </div>
</template>

<script>
import FrontPageDesignNav from '@/components/front_page/designs/FrontPageDesignNav.vue'
import FrontPageDesignFooter from '@/components/front_page/designs/FrontPageDesignFooter.vue'
import { BRAND, HERO, PILLARS, SHOWCASE } from '@/components/front_page/designs/shared'

export default {
  name: 'DesignTauri',
  components: { FrontPageDesignNav, FrontPageDesignFooter },
  data() {
    return {
      logo: BRAND.logo,
      hero: HERO,
      pillars: PILLARS,
      showcase: SHOWCASE,
    }
  },
  methods: {
    goToLogin() { this.$router.push('/login') },
    goToRegister() { this.$router.push({ path: '/login', query: { tab: 'register' } }) },
  },
}
</script>

<style scoped>
.design-tauri {
  min-height: 100vh;
  background: #0f0f0f;
  color: #e8e8e8;
  font-family: system-ui, -apple-system, sans-serif;
}

.hero {
  position: relative;
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 120px 24px 80px;
  text-align: center;
}

.hero__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
}

.hero__glow--1 {
  width: 500px;
  height: 500px;
  background: rgba(6, 119, 185, 0.35);
  top: -100px;
  left: 10%;
}

.hero__glow--2 {
  width: 400px;
  height: 400px;
  background: rgba(123, 172, 214, 0.2);
  bottom: 0;
  right: 15%;
}

.hero__content {
  position: relative;
  max-width: 780px;
}

.hero__logo {
  height: 52px;
  margin-bottom: 40px;
  filter: brightness(0) invert(1);
  opacity: 0.95;
}

.hero__title {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 24px;
  letter-spacing: -0.02em;
}

.hero__accent {
  background: linear-gradient(90deg, #7bacd6, #0677b9);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero__tagline {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0 0 40px;
}

.hero__actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  padding: 14px 28px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: transform 0.15s;
}

.btn:hover { transform: translateY(-2px); }

.btn--primary {
  background: linear-gradient(135deg, #0677b9, #055a8c);
  color: #fff;
  box-shadow: 0 8px 32px rgba(6, 119, 185, 0.4);
}

.btn--ghost {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.features {
  padding: 80px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.features__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.feature-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
  transition: border-color 0.2s, transform 0.2s;
}

.feature-card:hover {
  border-color: rgba(123, 172, 214, 0.4);
  transform: translateY(-4px);
}

.feature-card__icon {
  width: 44px;
  height: 44px;
  margin-bottom: 16px;
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.feature-card h3 {
  font-size: 1.1rem;
  margin: 0 0 12px;
}

.feature-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feature-card__tags span {
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(6, 119, 185, 0.2);
  color: #7bacd6;
}

.showcase {
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.showcase-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  margin-bottom: 80px;
}

.showcase-row--reverse {
  direction: rtl;
}

.showcase-row--reverse > * {
  direction: ltr;
}

.showcase-row__text h2 {
  font-size: 1.75rem;
  margin: 0 0 16px;
}

.showcase-row__text p {
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.7;
  margin: 0;
}

.showcase-row__img {
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .showcase-row {
    grid-template-columns: 1fr;
  }
  .showcase-row--reverse {
    direction: ltr;
  }
}
</style>
